import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";
import {
  FeedbackPriority,
  FeedbackStatus,
  type Feedback as FeedbackT,
  type FeedbackPriority as FeedbackPriorityT,
  type Palette,
} from "@heraion/shared";
import type { FeedbackPatchBody } from "./useFeedbackInbox";
import { priorityColors, type InboxStyles } from "./styles";
import { formatDateTime } from "@heraion/shared";
import { useDateLocale } from "../primitives/DateFormatContext";
import { stateAria } from "../a11y";

// Null = "untriaged"; offered first so the admin can clear a priority.
const PRIORITY_OPTIONS: readonly (FeedbackPriorityT | null)[] = [
  null,
  ...FeedbackPriority.options,
];

type Props = {
  item: FeedbackT;
  expanded: boolean;
  busy: boolean;
  onToggle: () => void;
  onPatch: (id: string, body: FeedbackPatchBody) => void;
  styles: InboxStyles;
  palette: Palette;
};

export function FeedbackRow({
  item,
  expanded,
  busy,
  onToggle,
  onPatch,
  styles,
  palette,
}: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const pri = priorityColors(palette, item.priority);
  const statusChipColors =
    item.status === "shipped"
      ? { bg: palette.completeFill, fg: palette.complete }
      : item.status === "wontfix"
        ? { bg: palette.surface2, fg: palette.ink3 }
        : { bg: palette.surface2, fg: palette.ink2 };

  return (
    <View style={styles.row}>
      <Pressable
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        {...stateAria({ expanded })}
        style={styles.rowHead}
      >
        <View style={styles.rowHeadMeta}>
          <View style={[styles.priBadge, { backgroundColor: pri.bg }]}>
            <Text
              style={[styles.priText, { color: pri.fg }]}
              accessibilityLabel={
                item.priority
                  ? undefined
                  : t("feedback.priorityNone", { defaultValue: "None" })
              }
            >
              {item.priority ? item.priority.toUpperCase() : "—"}
            </Text>
          </View>
          <Text style={styles.kindText}>{t(`feedback.kind_${item.kind}`)}</Text>
          <Text style={styles.metaText}>{item.surface}</Text>
          <View
            style={[styles.statusChip, { backgroundColor: statusChipColors.bg }]}
          >
            <Text
              style={[styles.statusChipText, { color: statusChipColors.fg }]}
            >
              {t(`feedback.status_${item.status}`)}
            </Text>
          </View>
          <Text style={styles.emailText}>{item.submitterEmail ?? "\u2014"}</Text>
          <Text style={styles.metaText}>
            {formatDateTime(new Date(item.createdAt), dateLocale)}
          </Text>
        </View>
        {/* Collapsed preview so the list is scannable without opening each
            row — the full body still shows expanded below. */}
        {expanded ? null : (
          <Text style={styles.snippet} numberOfLines={2}>
            {item.body}
          </Text>
        )}
      </Pressable>

      {expanded ? (
        <View style={styles.rowBody}>
          <Text style={styles.bodyText}>{item.body}</Text>
          <Text style={styles.metaText}>
            {item.appVersion}
            {item.lastScreen ? ` · ${item.lastScreen}` : ""}
            {item.device ? ` · ${item.device}` : ""}
          </Text>

          <View style={styles.controlGroup}>
            <Text style={styles.controlLabel}>
              {t("feedback.priorityLabel")}
            </Text>
            <View style={styles.controlRow}>
              {PRIORITY_OPTIONS.map((p) => {
                const active = item.priority === p;
                return (
                  <Pressable
                    key={p ?? "none"}
                    disabled={busy}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    {...stateAria({ selected: active }, "button")}
                    onPress={() => onPatch(item.id, { priority: p })}
                    style={[styles.chip, active && styles.chipActive]}
                  >
                    <Text
                      style={[styles.chipText, active && styles.chipTextActive]}
                    >
                      {p ? p.toUpperCase() : t("feedback.priorityNone")}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View style={styles.controlGroup}>
            <Text style={styles.controlLabel}>{t("feedback.statusLabel")}</Text>
            <View style={styles.controlRow}>
              {FeedbackStatus.options.map((s) => {
                const active = item.status === s;
                return (
                  <Pressable
                    key={s}
                    disabled={busy}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    {...stateAria({ selected: active }, "button")}
                    onPress={() => onPatch(item.id, { status: s })}
                    style={[styles.chip, active && styles.chipActive]}
                  >
                    <Text
                      style={[styles.chipText, active && styles.chipTextActive]}
                    >
                      {t(`feedback.status_${s}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </View>
      ) : null}
    </View>
  );
}
